import { getAuthentication } from '@/hooks/useAuthentication';

const API_URL = import.meta.env.VITE_API_URL;

const getAuthHeaders = (): HeadersInit => {
  const auth = getAuthentication();
  const token = auth?.accessToken;

  return token ? { Authorization: `Bearer ${token}` } : {};
};

export const getPreviewUrl = (documentId: string): string => {
  return `${API_URL}/api/resource-asset/${documentId}/preview`;
};

export const getDownloadUrl = (documentId: string): string => {
  return `${API_URL}/api/resource-asset/${documentId}/download`;
};

export const fetchPreviewBlob = async (documentId: string): Promise<{ blob: Blob; mimeType: string }> => {
  const url = getPreviewUrl(documentId);

  const response = await fetch(url, {
    method: 'GET',
    headers: getAuthHeaders(),
  });

  if (!response.ok) {
    throw new Error(`Failed to fetch preview: ${response.status} ${response.statusText}`);
  }

  const blob = await response.blob();
  const mimeType = response.headers.get('Content-Type') || 'application/octet-stream';

  return { blob, mimeType };
};

export const triggerDownload = async (documentId: string): Promise<void> => {
  const url = getDownloadUrl(documentId);

  const response = await fetch(url, {
    method: 'GET',
    headers: getAuthHeaders(),
    redirect: 'follow',
  });

  if (!response.ok) {
    throw new Error(`Failed to download: ${response.status} ${response.statusText}`);
  }

  const data = (await response.json()) as { downloadUrl: string };
  window.open(data.downloadUrl, '_blank');
};

export const triggerBulkDownload = async (assetDocumentIds: string[]): Promise<void> => {
  const url = `${API_URL}/api/resource-asset/bulk-download`;

  const response = await fetch(url, {
    method: 'POST',
    headers: {
      ...getAuthHeaders(),
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ assetDocumentIds }),
  });

  if (!response.ok) {
    throw new Error(`Failed to download: ${response.status} ${response.statusText}`);
  }

  const data = (await response.json()) as { downloadUrl: string };
  window.open(data.downloadUrl, '_blank');
};

export const isPreviewableAsset = (mime?: string, ext?: string): boolean => {
  if (!mime && !ext) return false;

  const previewableMimes = [
    'application/pdf',
    'image/jpeg',
    'image/png',
    'image/gif',
    'image/webp',
    'image/svg+xml',
    'text/plain',
    'text/html',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  ];

  const previewableExtensions = ['.pdf', '.jpg', '.jpeg', '.png', '.gif', '.webp', '.svg', '.txt', '.html', '.docx'];

  if (mime && previewableMimes.some((m) => mime.includes(m.split('/')[1]) || mime === m)) {
    return true;
  }

  if (ext && previewableExtensions.includes(ext.toLowerCase())) {
    return true;
  }

  return false;
};

export const getPreviewContentType = (mime?: string, ext?: string): 'pdf' | 'image' | 'text' | 'html' | 'unknown' => {
  if (mime?.includes('pdf') || ext === '.pdf') return 'pdf';
  if (mime?.includes('image') || ['.jpg', '.jpeg', '.png', '.gif', '.webp', '.svg'].includes(ext || '')) return 'image';
  if (mime?.includes('text/plain') || ext === '.txt') return 'text';
  if (
    mime?.includes('text/html') ||
    ext === '.html' ||
    mime?.includes('wordprocessingml') ||
    ext === '.docx' ||
    ext === '.doc'
  ) {
    return 'pdf';
  }

  return 'unknown';
};
